import * as React from 'react';
import propz from 'propz';
import { Component } from 'react';
import moment from 'moment';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { uploadImage } from '../../helpers/service/image';
import { AppUser } from 'Src/views/App/App';
import { News } from '../../models/news';
import './NewsForm.css';
import { HTMLEditorQuillForNews } from '../HTMLEditorQuill/HTMLEditorQuillForNews';
import { DatePicker as SitDatePicker } from '../DatePicker/DatePicker';

interface Props {
  onCloseClick: () => void;
  onFormSubmit: (news: any) => void;
  user: AppUser;
  news?: News;
}

interface State {
  title: string;
  body: string;
  date: Date | null;
  picUrl: string;
  isImageError: boolean;
}

const NewsValidationSchema = Yup.object().shape({
  title: Yup.string().required('Required'),
  body: Yup.string().required('Required'),
  date: Yup.date()
    .nullable()
    .required('Required')
});

export class NewsForm extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const isNewsExist = this.isNewsExist();
    const { news } = this.props;

    this.state = {
      title: isNewsExist ? propz.get(news, ['title'], '') : '',
      body: isNewsExist ? propz.get(news, ['body'], '') : '',
      date: isNewsExist ? this.getNewsDate() : null,
      picUrl: isNewsExist ? propz.get(news, ['picUrl'], '') : '',
      isImageError: false
    };
  }

  getNewsDate(): Date | null {
    const { news } = this.props;
    return typeof news.date !== 'undefined' ? moment(news.date).toDate() : null;
  }

  isNewsExist() {
    const { news } = this.props;

    return typeof news !== 'undefined';
  }

  onImageSelected = event => {
    const image = event.target.files[0];

    uploadImage(this.props.user, image)
      .then(response => {
        const picUrl = `${window.apiImg}/images/${response.key}`;
        this.setState({
          picUrl: picUrl,
          isImageError: false
        });
      })
      .catch(error => {
        console.error(error);
        this.setState({ isImageError: true });
      });
  };

  onSubmit = data => {
    //prepare data
    const { date, title, body } = data;
    const isoDate = date.toISOString();
    const news = {
      picUrl: this.state.picUrl,
      date: isoDate,
      body: body,
      title: title
    };

    this.props.onFormSubmit(news);
  };

  onRemoveImageClick = (): void => {
    this.setState({ picUrl: '' });
  };

  render() {
    const { isImageError, picUrl } = this.state;
    const imgInputClassName = isImageError ? 'newsEditor__file is-invalid' : 'newsEditor__file';

    const isNewsExist = this.isNewsExist();

    const isPicUrlExists = typeof picUrl !== 'undefined' && picUrl.length > 0;

    return (
      <Formik
        onSubmit={this.onSubmit}
        initialValues={{
          title: this.state.title,
          date: this.state.date,
          body: this.state.body
        }}
        validationSchema={NewsValidationSchema}
      >
        {({ values, setFieldValue, touched, errors }) => (
          <Form className="newsEditor">
            <div className="container-fluid pl-0">
              <div className="row">
                <div className="col-md-10">
                  <div className="form-group">
                    <label htmlFor="news-title">Title</label>
                    <Field
                      id="news-title"
                      type="text"
                      name="title"
                      className={`${touched.title && errors.title ? 'form-control is-invalid' : 'form-control '}`}
                      placeholder="Enter title"
                    />
                    {touched.title && errors.title ? (
                      <div className="invalid-feedback">{String(errors.title)}</div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>

            <div className="container-fluid pl-0">
              <div className="row">
                <div className="col-md-4">
                  <div className="form-group">
                    <SitDatePicker
                      label="Publish date and time"
                      mode="datetime"
                      value={values.date}
                      onChange={date => setFieldValue('date', date)}
                      error={touched.date && errors.date ? String(errors.date) : undefined}
                      format="DD MMM YYYY, HH:mm"
                      minuteStep={5}
                      required
                      clearable={false}
                      className="newsEditor__datePicker"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="container-fluid pl-0">
              <div className="row">
                <div className="col-md-12">
                  <div className="form-group">
                    <label>Content</label>
                    <div className="eNewsTextEditor newsEditor__richText">
                      <HTMLEditorQuillForNews
                        user={this.props.user}
                        onTextChange={body => {
                          setFieldValue('body', body);
                        }}
                        value={values.body}
                        isEnabled={true}
                        placeholder="Enter body"
                      />
                      {touched.body && errors.body ? (
                        <div className="invalid-feedback">{String(errors.body)}</div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="news-image">Featured image</label>
              <input
                id="news-image"
                type="file"
                name="picUrl"
                accept="image/*"
                className={imgInputClassName}
                onChange={this.onImageSelected}
              />
              {isImageError && <div className="invalid-feedback">Error uploading image</div>}
            </div>

            {isPicUrlExists && (
              <div className="newsEditor__preview">
                <button
                  type="button"
                  className="newsEditor__removeImage"
                  onClick={this.onRemoveImageClick}
                  aria-label="Remove image"
                >
                  <span>&times;</span>
                </button>
                <img className="img-fluid img-thumbnail" src={picUrl} alt="News picture" />
              </div>
            )}
            <div className="newsEditor__actions">
              <button type="button" className="newsEditor__cancel" onClick={this.props.onCloseClick}>
                Cancel
              </button>
              <button type="submit" className="newsEditor__save">
                Save news
              </button>
            </div>
          </Form>
        )}
      </Formik>
    );
  }
}
